"use client";

import { useEffect, useState } from "react";
import Image from "next/image";
import { AnimatePresence, motion } from "motion/react";
import { useLocale, useTranslations } from "next-intl";
import { Link } from "@/i18n/navigation";
import SolarIcon from "@/components/ui/SolarIcon/SolarIcon";
import { getCourseBySlug, getCourseTitle } from "@/lib/courses-catalog";

const EASE = [0.22, 1, 0.36, 1];
// Formation fermée par l'apprenant : mémorisée par slug, donc la carte
// revient d'elle-même à la sortie de la formation suivante.
const DISMISS_KEY = "blvmy_latest_course_dismissed";

const readDismissed = () => {
    try {
        return window.localStorage.getItem(DISMISS_KEY);
    } catch {
        return null;
    }
};

/**
 * Annonce automatique de la dernière formation sortie (colonne de droite du
 * tableau de bord). Source : /api/courses/get/courses, qui renvoie les
 * formations en ligne de la plus récente à la plus ancienne ; on écarte
 * celles que le catalogue annonce « bientôt ».
 *
 * Affichée à tout le monde, sauf à qui a déjà rejoint (ou archivé) la
 * formation, jusqu'à ce que l'apprenant la ferme. Elle partage sa place avec la carte d'essai
 * Pro : `onVisibilityChange(false)` (fermée, ou rien à annoncer) laisse le
 * tableau de bord afficher la carte Pro à sa place. On ne se rabat jamais
 * sur une formation plus ancienne.
 */
export default function LatestCourseCard({ ownedSlugs = [], onVisibilityChange }) {
    const t = useTranslations();
    const locale = useLocale();
    const [course, setCourse] = useState(null);
    const [hidden, setHidden] = useState(false);

    const report = (visible) => onVisibilityChange?.(visible);
    const ownedKey = ownedSlugs.join("|");

    useEffect(() => {
        let cancelled = false;
        fetch("/api/courses/get/courses")
            .then((response) => (response.ok ? response.json() : null))
            .then((data) => {
                if (cancelled) return;
                if (!Array.isArray(data?.courses)) return report(false);
                const latest = data.courses.find(
                    (item) => item?.slug && !getCourseBySlug(item.slug)?.isSoon,
                );
                if (
                    !latest ||
                    ownedKey.split("|").includes(latest.slug) ||
                    readDismissed() === latest.slug
                ) {
                    return report(false);
                }

                const catalog = getCourseBySlug(latest.slug);
                setCourse({
                    slug: latest.slug,
                    title: catalog ? getCourseTitle(catalog, locale) : latest.name,
                    // Seule l'icône du catalogue est affichée : l'image du
                    // back n'est pas toujours une URL servable.
                    image: catalog?.image || null,
                    // Catalogue rédigé en français : description et durée
                    // seulement en français.
                    description:
                        locale === "fr" ? catalog?.description || null : null,
                    duration: locale === "fr" ? catalog?.duration || null : null,
                    level: catalog?.level || null,
                });
                report(true);
            })
            .catch(() => {
                if (!cancelled) report(false);
            });
        return () => {
            cancelled = true;
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [locale, ownedKey]);

    const dismiss = () => {
        try {
            window.localStorage.setItem(DISMISS_KEY, course.slug);
        } catch {
            // Stockage indisponible : la carte revient au prochain chargement.
        }
        setHidden(true);
        report(false);
    };

    const levelLabels = {
        beginner: t("course_beginner"),
        intermediate: t("course_intermediate"),
        advanced: t("course_advanced"),
    };

    return (
        <AnimatePresence initial={false}>
            {course && !hidden && (
                <motion.div
                    key={course.slug}
                    initial={{ opacity: 0, y: 8 }}
                    animate={{ opacity: 1, y: 0, height: "auto" }}
                    exit={{ opacity: 0, height: 0, marginTop: 0 }}
                    transition={{ duration: 0.3, ease: EASE }}
                    className="mt-4 overflow-hidden"
                >
                    <div className="rounded-2xl border border-gray-200/70 bg-white p-4 shadow-sm dark:border-borderDark dark:bg-bgDark-840">
                        <div className="flex items-center justify-between gap-3">
                            <span className="inline-flex items-center gap-1.5 rounded-full bg-primary/10 px-2 py-0.5 text-[11px] font-semibold text-primary">
                                <span className="h-1.5 w-1.5 rounded-full bg-primary" />
                                {t("dashboard_latest_course_label")}
                            </span>
                            <button
                                type="button"
                                onClick={dismiss}
                                aria-label={t("dashboard_latest_course_dismiss")}
                                title={t("dashboard_latest_course_dismiss")}
                                className="-mr-1 flex h-7 w-7 items-center justify-center rounded-lg text-gray-400 transition-colors hover:bg-gray-100 hover:text-gray-700 dark:text-grayDark-500 dark:hover:bg-white/5 dark:hover:text-white"
                            >
                                <SolarIcon
                                    name="close-circle-linear"
                                    className="text-base"
                                />
                            </button>
                        </div>

                        <Link
                            href={`/course/${course.slug}`}
                            className="group mt-3 flex items-start gap-3"
                        >
                            {course.image ? (
                                <Image
                                    src={course.image}
                                    alt=""
                                    width={56}
                                    height={56}
                                    className="h-14 w-14 flex-shrink-0 rounded-xl object-contain transition-transform duration-300 group-hover:scale-105"
                                />
                            ) : (
                                <span className="flex h-14 w-14 flex-shrink-0 items-center justify-center rounded-xl bg-gray-100 text-gray-500 dark:bg-white/5 dark:text-grayDark-400">
                                    <SolarIcon
                                        name="square-academic-cap-linear"
                                        className="text-2xl"
                                    />
                                </span>
                            )}
                            <div className="min-w-0">
                                <div className="line-clamp-2 text-[15px] font-semibold leading-snug text-gray-900 group-hover:underline dark:text-white">
                                    {course.title}
                                </div>
                                {(course.level || course.duration) && (
                                    <div className="mt-1 flex flex-wrap items-center gap-x-2 gap-y-0.5 text-xs text-gray-500 dark:text-grayDark-400">
                                        {course.level && levelLabels[course.level] && (
                                            <span>{levelLabels[course.level]}</span>
                                        )}
                                        {course.level && course.duration && (
                                            <span className="text-gray-300 dark:text-grayDark-600">
                                                ·
                                            </span>
                                        )}
                                        {course.duration && (
                                            <span>{course.duration}</span>
                                        )}
                                    </div>
                                )}
                            </div>
                        </Link>

                        {course.description && (
                            <p className="mb-0 mt-3 text-sm leading-snug text-gray-600 dark:text-grayDark-300">
                                {course.description}
                            </p>
                        )}

                        <Link
                            href={`/course/${course.slug}`}
                            className="btn-3d mt-4 flex w-full items-center justify-center gap-1.5 rounded-xl bg-primary px-4 py-2.5 text-sm font-semibold text-white transition hover:brightness-95 hover:text-white"
                        >
                            {t("dashboard_latest_course_cta")}
                            <SolarIcon
                                name="alt-arrow-right-linear"
                                className="text-base"
                            />
                        </Link>
                    </div>
                </motion.div>
            )}
        </AnimatePresence>
    );
}
